import {
    Button,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { Add, Delete, Payment } from "@material-ui/icons"
import ReaisMask from "../../../../masks/reais"

const useStyles = makeStyles(theme => ({
    footer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        [theme.breakpoints.up("sm")]: {
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
        },
    },
    totais: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "0.4rem 0.8rem",
        marginTop: "0.8rem",
        [theme.breakpoints.up("sm")]: {
            marginTop: 0,
        },
    },
}))

const formatBRL = valor =>
    Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valor ?? 0)

export default function EdicaoFormasPagamento({
    formasDisponiveis = [],
    valorTotalPedido = 0,
    formas = [],
    setFormas,
    desabilitado = false,
}) {
    const classes = useStyles()

    const opcoes = (formasDisponiveis || []).filter(forma => forma.ativo && !forma.online)

    const totalInformado = formas.reduce((acc, f) => acc + (Number(f.valor) || 0), 0)
    const diferenca = Number((valorTotalPedido - totalInformado).toFixed(2))
    const multiplas = formas.length > 1

    const encontrarForma = id => opcoes.find(forma => forma.id === Number(id))

    const alterarForma = (index, id) => {
        const forma = encontrarForma(id)
        setFormas(
            formas.map((item, i) =>
                i === index
                    ? {
                          ...item,
                          formaPagamento: { id: Number(id) },
                          descricaoFormaPagamento: forma?.descricao ?? item.descricaoFormaPagamento,
                      }
                    : item
            )
        )
    }

    const alterarValor = (index, valor) => {
        setFormas(formas.map((item, i) => (i === index ? { ...item, valor } : item)))
    }

    const adicionarForma = () => {
        const forma = opcoes[0]
        setFormas([
            ...formas,
            {
                formaPagamento: { id: forma?.id },
                descricaoFormaPagamento: forma?.descricao ?? "",
                valor: diferenca > 0 ? diferenca : 0,
                pago: false,
            },
        ])
    }

    const removerForma = index => {
        setFormas(formas.filter((_, i) => i !== index))
    }

    return (
        <div
            style={{
                display: "block",
                width: "100%",
                maxWidth: "100%",
                overflowX: "hidden",
                boxSizing: "border-box",
            }}
        >
            <Typography variant="subtitle2" style={{ marginBottom: "0.8rem" }}>
                Formas de pagamento
            </Typography>

            {formas.map((item, index) => {
                const mostrarRemover = formas.length > 1 && !desabilitado
                return (
                    <Grid
                        container
                        alignItems="center"
                        style={{ marginBottom: "0.8rem", width: "100%" }}
                    >
                        <Grid
                            item
                            xs={12}
                            sm={5}
                            style={{ paddingRight: 8, boxSizing: "border-box" }}
                        >
                            <FormControl
                                variant="outlined"
                                disabled={desabilitado || opcoes.length < 1}
                                fullWidth
                            >
                                <InputLabel style={{ textWrap: "nowrap" }}>Forma</InputLabel>
                                <Select
                                    label="Forma"
                                    value={item?.formaPagamento?.id ?? ""}
                                    onChange={e => alterarForma(index, e.target.value)}
                                >
                                    {opcoes.map(forma => (
                                        <MenuItem key={forma.id} value={forma.id}>
                                            <span>
                                                {forma.urlImagem ? (
                                                    <img
                                                        src={forma.urlImagem}
                                                        alt={forma.descricao}
                                                        width="18px"
                                                    />
                                                ) : (
                                                    <Payment />
                                                )}
                                                {forma.descricao}
                                            </span>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm style={{ boxSizing: "border-box" }}>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                }}
                            >
                                <div
                                    style={{
                                        flex: mostrarRemover ? "0 0 70%" : "1 1 100%",
                                        maxWidth: mostrarRemover ? "70%" : "100%",
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        label="Valor"
                                        value={item.valor ?? 0}
                                        disabled={desabilitado}
                                        onChange={e => alterarValor(index, e.floatValue)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">R$</InputAdornment>
                                            ),
                                            inputComponent: ReaisMask,
                                        }}
                                        inputProps={{ style: { textAlign: "right" } }}
                                    />
                                </div>

                                {mostrarRemover ? (
                                    <div
                                        style={{
                                            flex: "0 0 30%",
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <IconButton
                                            aria-label="remover forma"
                                            onClick={() => removerForma(index)}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </div>
                                ) : null}
                            </div>
                        </Grid>
                    </Grid>
                )
            })}

            <div className={classes.footer}>
                <div>
                    <Tooltip
                        arrow
                        title={opcoes.length < 1 ? "Nenhuma forma de pagamento disponível" : ""}
                    >
                        <span>
                            <Button
                                startIcon={<Add />}
                                onClick={adicionarForma}
                                disabled={desabilitado || opcoes.length < 1}
                            >
                                Adicionar forma
                            </Button>
                        </span>
                    </Tooltip>
                </div>

                {multiplas && valorTotalPedido > 0 ? (
                    <div className={classes.totais}>
                        <Typography variant="caption">
                            Total informado: {formatBRL(totalInformado)} de{" "}
                            {formatBRL(valorTotalPedido)}
                        </Typography>
                        <Typography
                            variant="caption"
                            style={{ color: Math.abs(diferenca) > 0.01 ? "var(--cor-erro)" : "#28a745" }}
                        >
                            {Math.abs(diferenca) > 0.01
                                ? diferenca > 0
                                    ? `Falta ${formatBRL(diferenca)}`
                                    : `Excede ${formatBRL(Math.abs(diferenca))}`
                                : "Soma confere"}
                        </Typography>
                    </div>
                ) : null}
            </div>
        </div>
    )
}
